import { Box, Button, FormControlLabel, Switch } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FBApprovalsProps, FBAutocompleteAsync, FBAutocompleteAsyncOption, FBButton, FBDialog, FBInlineApprovalBody, FBInlineApprovalStatusIdent, FBInput, FBMapWrapper, FBProvider, FBRequiredApprover, FBSection, FBText, Owner } from '..';
import { MomentFormats, useDate } from '../../../common/utils/date';
import { authSelectors } from '../../../state/ducks/auth';
import { Approval, ApprovalStatus } from '../../../state/ducks/common/types';
import { documentRevisionsActions, documentRevisionsSelectors } from '../../../state/ducks/documentRevisions';
import { DocumentStatus } from '../../../state/ducks/documents/types';
import ApproverConfigurationDialog from '../../administration/general.settings/components/configurationDialog/ApproverConfigurationDialog';
import useStyles from '../../administration/general.settings/components/configurationDialog/dialog.styles';
import { evaluateRequirements } from '../../administration/general.settings/panels/ApprovalConfigurations/helpers';
import AlertDialog from '../../app/alert.dialog/AlertDialog';
import ApprovalWorkflow from '../../components/common/approval.workflow/ApprovalWorkflow';
import { toastError } from '../../components/notifications';
import { isDocumentRevisionApproved } from '../../documentRevision/helpers/documentRevisionStatus';
import useActionCreator from '../../hooks/useActionCreator';
import useAsync from '../../hooks/useAsync';
import useDialog from '../../hooks/useDialog';
import { StyledCancelFBButton } from '../FBButton/StyledCancelFBButton';
import ApprovalApprovedGroup from './ApprovalApprovedGroup';
import { withFBApprovals } from './FBApprovals.wrap';

const FBApprovals: React.FunctionComponent<FBApprovalsProps> = ({
  onApproveClick,
  onRejectClick,
  dialogState,
  requestApproval,
  approvals = [],
  approvalTransition,
  currentUser,
  disabled,
  docId,
  documentStatus,
  groups = {},
  isIncluded,
  label,
  name,
  ownerId,
  renderOption,
  loading,
  inlineApprovalExists,
  isAlphaReleased,
  disabledOptions,
  disableChipDelete,
  owners,
  showFieldOwnersWarning,
  isContributorIncluded,
  autosave,
  approvalApprovedGroup,
  isGRPT = false,
  documentRevisionId,
  isApprovalEditEnabled,
  approvalStages,
  workflowStatusDetails,
  ...props
}) => {
  const { getFormattedDateString } = useDate();
  const [isAdvancedEnabled, setIsAdvancedEnabled] = useState<boolean>(approvalStages.length > 0);
  const isApproverSelectionDisabled = disabled || isAdvancedEnabled;

  const classes = useStyles();

  const checkedGroups = useMemo(
    () => evaluateRequirements(approvalStages, groups),
    [approvalStages, groups],
  );

  const artifactHasMappedFields = useSelector(
    documentRevisionsSelectors.makeHasEditableApprovalStageFields(documentRevisionId),
  );

  const currentEmployeeId = useSelector(authSelectors.currentEmployeeId);
  const configureDialog = useDialog();

  const loadDocRevAction = useActionCreator(documentRevisionsActions.load);

  const unlinkConnectedApprovalsAction = useActionCreator(documentRevisionsActions.unlinkConnectedApprovals);
  const unlinkConnectedApprovalsAsync = useAsync({
    onError: (error) => {
      toastError(error);
    },
    onSuccess: () => {
      loadDocRevAction(documentRevisionId);
    },
  });
  function unlinkConnectedApprovals () {
    unlinkConnectedApprovalsAsync.start(
      unlinkConnectedApprovalsAction,
      documentRevisionId,
      unlinkConnectedApprovalsAsync,
    );
  }

  const enableAdvancedDialog = useDialog();
  const cancelEnableAdvancedDialog = () => {
    enableAdvancedDialog.close();
  };
  const onConfirmEnableAdvanced = () => {
    setIsAdvancedEnabled(true);
    unlinkConnectedApprovals();
    enableAdvancedDialog.close();
  };

  const disableAdvancedDialog = useDialog();
  const cancelDisableAdvancedDialog = () => {
    disableAdvancedDialog.close();
  };
  const onConfirmDisableAdvanced = () => {
    setIsAdvancedEnabled(false);
    unlinkConnectedApprovals();
    disableAdvancedDialog.close();
  };

  const toggleAdvancedConfiguration = (isAdvancedChecked: boolean) => {
    if (!isApprovalEditEnabled) {
      return;
    }
    if (isAdvancedChecked) {
      if (approvals?.length) {
        enableAdvancedDialog.open();
      } else {
        setIsAdvancedEnabled(true);
      }
    }

    if (!isAdvancedChecked) {
      disableAdvancedDialog.open();
    }
  };

  if (isGRPT && disabled) {
    return null;
  }

  const handleConfirm = () => {
    loadDocRevAction(documentRevisionId);
  };

  return (
    <FBProvider {...{ dialogState }}>
      <FBDialog />
      <FBInput {...props} {...{ name, autosave }} type="approvals">
        <FBSection
          name={`fb-approvals-section-${name}`}
          {...{ label }}
        />
        { !isAdvancedEnabled && <FBAutocompleteAsync
          placeholder="form.builder.start.typing.list"
          label="form.builder.approvers"
          name={`fb-approvals-${name}`}
          optionId={FBAutocompleteAsyncOption.availableApprovers}
          withState
          gutter={false}
          multiple={true}
          autosave={false}
          disabled = {isApproverSelectionDisabled}
          {...{ renderOption, loading, disabledOptions, disableChipDelete }}
        /> }
        <Box display="flex" flexWrap="wrap" my={2} mt={1}>
          {approvalApprovedGroup ? (
            approvalApprovedGroup.map((groupName: string, index: number) => (
              <ApprovalApprovedGroup groupName={groupName} key={index} isChecked />
            ))
          ) : (
            <FBMapWrapper<Record<string, FBRequiredApprover>> collection={groups}>
              {(group: FBRequiredApprover, id) => (
                <ApprovalApprovedGroup
                  groupName={group.name}
                  key={id}
                  isChecked={isIncluded?.(group) || (checkedGroups.findIndex(group => group.id === id) > -1)}
                />
              )}
            </FBMapWrapper>
          )}
          <Box className={isAdvancedEnabled ? classes.switchControlEnabled : classes.switchControlDisabled}>
            {isApprovalEditEnabled && <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isAdvancedEnabled}
                  onChange={(event) => toggleAdvancedConfiguration(event.target.checked)}
                  color="primary"
                  classes={{
                    switchBase: classes.switchBase,
                  }}
                />
              }
              label="Advanced"
              className={classes.mappingSwitcher}
              labelPlacement="end"
            /> }
            {isAdvancedEnabled && isApprovalEditEnabled && (
              <Button
                variant="text"
                size="small"
                onClick={configureDialog.open}
              > Configure
              </Button>
            )}
          </Box>
        </Box>
        {showFieldOwnersWarning && owners && <Box display="flex" flexWrap="wrap" mb={4} mt={1}>
          <FBMapWrapper<Owner[]> collection={owners}>
            {(owner: Owner, id) => (
              <Box display="flex" key={`fb-approvals-${id}`} mr={2.5} >
                <CheckCircleIcon
                  data-cy={
                  `fb-approvals-${owner.name}-check-${isContributorIncluded?.(owner.id) ? 'marked' : 'disabled'}`
                  }
                  color={isContributorIncluded?.(owner.id) ? 'primary' : 'disabled'}
                />
                <FBText boxProps={{ mt: 0.2, pl: 0.5 }} locale={owner.name} />
              </Box>
            )}
          </FBMapWrapper>
        </Box>}
        {!isAdvancedEnabled && <FBMapWrapper<FBInlineApprovalBody[]> collection={approvals}>
          {(approval: FBInlineApprovalBody, index) => {
            const isApproved = isDocumentRevisionApproved(approval.status);
            return (
              <Box
                data-cy={isApproved ? 'approved-' + approval.approverName
                  : 'not-approved-' + approval.approver?.user.name}>
                <Box
                  key={`fb-ia-info-${approval.id}-${index}`}
                  pb={currentUser === approval.approver?.user.id ? 0 : 0.6}
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                >
                  <Box fontWeight="bold">{
                    isApproved ? approval.approverName : approval.approver?.user.name
                  }</Box>
                  {approval.status && approval.approverGroup && (
                    <Box>&nbsp;| {approval.approverGroup?.join(', ')}</Box>
                  )}
                  {approval.status && (
                    <Box>&nbsp;| {FBInlineApprovalStatusIdent[approval.status]}</Box>
                  )}
                  {approval.reviewedAt && isApproved && (
                    <Box>&nbsp;| {getFormattedDateString(approval.reviewedAt, MomentFormats.DateTime)}</Box>
                  )}
                  {approval.canRequest && inlineApprovalExists && !isAlphaReleased && (
                    <>
                      <Box> |</Box>
                      <FBButton
                        style={{ margin: 0 }}
                        label="form.builder.request.approval"
                        variant="text"
                        color="primary"
                        onClick={() => requestApproval?.(approval.id)}
                      />
                    </>
                  )}
                  {documentStatus === DocumentStatus.InReview
                && approval.status === ApprovalStatus.Pending
                && currentUser === approval.approver?.user.id
                && (
                  <Box flexGrow={1} textAlign="right">
                    <StyledCancelFBButton
                      {...{ disabled }}
                      label="common.reject"
                      size="small"
                      style={{ fontSize: '11px' }}
                      onClick={() => onRejectClick?.(approval.id || '')}
                    />
                    <FBButton
                      {...{ disabled }}
                      label="form.builder.request.approve"
                      size="small"
                      style={{ fontSize: '11px' }}
                      onClick={() => onApproveClick?.(approval.id || '')}
                    />
                  </Box>
                )}
                </Box>
                <Box mt={1} mb={4}>
                  { isApproved ? approval.approverEmail : approval.approver?.user.email }
                </Box>
              </Box>
            );
          }
          }
        </FBMapWrapper> }
        {isAdvancedEnabled && <ApprovalWorkflow
          approvals={approvals as Approval[]}
          currentEmpId={currentEmployeeId}
          ownerId={ownerId}
          workflowStages={approvalStages}
          workflowStatusDetails={workflowStatusDetails}
          documentRevisionId={documentRevisionId}
          isArtifactInReview={documentStatus === DocumentStatus.InReview}
          artifactHasMappedFields={artifactHasMappedFields}
          onApproveClick={onApproveClick}
          onRejectClick={onRejectClick}
          disabled={disabled}
        />}
        {configureDialog.isOpen && <ApproverConfigurationDialog
          onApprovalStagesUpdated={handleConfirm}
          requiredGroups={groups}
          documentRevisionId={documentRevisionId}
          isGetAllExistingConfigurations={true}
          isActionable={isApprovalEditEnabled}
          { ...{
            configureDialog,
            approvalApprovedGroup,
          }}
        />}
        {enableAdvancedDialog?.isOpen && (
          <AlertDialog
            dialog={enableAdvancedDialog}
            confirmAction={onConfirmEnableAdvanced}
            cancelAction={cancelEnableAdvancedDialog}
            message="approvals.switch.to.advanced.msg"
          />
        )}
        {disableAdvancedDialog?.isOpen && (
          <AlertDialog
            dialog={disableAdvancedDialog}
            confirmAction={onConfirmDisableAdvanced}
            cancelAction={cancelDisableAdvancedDialog}
            message="approvals.switch.from.advanced.msg"
          />
        )}
      </FBInput>
    </FBProvider>
  );
};

export default withFBApprovals(FBApprovals);
